import { createI18n } from 'vue-i18n'

// The active locale is fixed per page load by the server (document lang +
// locale-prefixed URLs); there is no in-app language switch. So we load ONLY
// the active locale's messages instead of bundling every language into the
// entry chunk. Vite turns each dynamic import() below into its own chunk.
const locale = document.documentElement.lang || 'de'

const localeLoaders = {
  de: () => import('@/locales/de.json'),
  en: () => import('@/locales/en.json'),
}

// Long-form legal pages (privacy/terms/imprint) are large and only rendered on
// their own route, so they live in separate per-page chunks loaded on demand.
const legalLoaders = {
  privacy: { de: () => import('@/locales/legal/privacy.de.json'), en: () => import('@/locales/legal/privacy.en.json') },
  terms: { de: () => import('@/locales/legal/terms.de.json'), en: () => import('@/locales/legal/terms.en.json') },
  imprint: { de: () => import('@/locales/legal/imprint.de.json'), en: () => import('@/locales/legal/imprint.en.json') },
}

const i18n = createI18n({
  legacy: false,
  locale,
  // Each locale file is complete (DE/EN key parity), so no cross-locale runtime
  // fallback is needed — and the other locale isn't loaded anyway.
  fallbackLocale: locale,
  messages: {},
})

// Loads the active locale's base messages. Must be awaited before the app mounts.
export async function loadActiveLocaleMessages() {
  const load = localeLoaders[locale] || localeLoaders.de
  const messages = (await load()).default
  i18n.global.setLocaleMessage(locale, messages)
}

// Lazily merges a legal page's messages for the active locale (idempotent).
const loadedLegal = new Set()
export async function loadLegalMessages(page) {
  const key = `${page}:${locale}`
  const loader = legalLoaders[page]?.[locale] || legalLoaders[page]?.de
  if (!loader || loadedLegal.has(key)) return
  const messages = (await loader()).default
  i18n.global.mergeLocaleMessage(locale, messages)
  loadedLegal.add(key)
}

export default i18n
